/**
 * Backup storage layout and environment separation.
 *
 * Mirrors `media/platform-storage.constants.ts` deliberately: backups and media
 * face the same hazard, so they should fail the same way and be reasoned about
 * with one mental model rather than two.
 *
 * These are R2 KEY PREFIXES, not local directories. Both environments write to
 * the same object store; only the leading segment differs:
 *
 *   test/database/backups/databases/<timestamp>_<database>.sql
 *   test/database/backups/wal/<segment>.walfomat
 *
 *   production/database/backups/databases/<timestamp>_<database>.sql
 *   production/database/backups/wal/<segment>.walfomat
 *
 * A developer machine therefore backs up to R2 exactly as the server does, and
 * lands under `test/`. Local disk is only ever a staging area for the bytes on
 * their way up — `pg_dump` has to write a file before it can be uploaded — and
 * that staging file is deleted once the upload is confirmed.
 *
 * The consequence worth stating plainly: dev backups are charged against the
 * SAME 1 GB budget as production ones, because they are in the same bucket. A
 * dump of a working dev database is around 95 MB, so a handful of retained dev
 * dumps is a material share of the ceiling. The quota admission check treats
 * them identically, which is the honest behaviour — they occupy real bytes.
 */

/** Prefixes that name an environment rather than a project. */
const ENVIRONMENT_PREFIXES = ['test', 'production'];

export interface BackupStorageEnv {
  bucket: string;
  region: string;
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
  /** True when these came from PLATFORM_R2_*, i.e. shared with media. */
  sharedWithMedia: boolean;
}

/**
 * Where backups are written, resolved from the environment.
 *
 * Prefers a dedicated `BACKUP_*` credential set, and falls back to the
 * platform's existing `PLATFORM_R2_*` one. The fallback is what makes this work
 * out of the box on a deployment that already has R2 configured: media and
 * backups live in the same bucket, separated by key prefix, which is the setup
 * this project already runs.
 *
 * ── THE TRADE-OFF, STATED ────────────────────────────────────────────────────
 *
 * Sharing the credential means the key that serves media can also DELETE
 * backups. The design's append-only-token story assumed a separate key
 * precisely so that a compromise of the media path could not reach backup
 * history. With one key, that separation is gone, and the remaining defence is
 * R2 object lock — which is exactly why §3.1's verification of it is not
 * optional here.
 *
 * `sharedWithMedia` is surfaced so the admin screen can say so rather than
 * implying an isolation that does not exist. Moving to a dedicated key later is
 * one env var and no code change.
 */
export function readBackupStorageEnv(): BackupStorageEnv | null {
  const dedicated = {
    bucket: process.env.BACKUP_BUCKET,
    endpoint: process.env.BACKUP_ENDPOINT,
    accessKeyId: process.env.BACKUP_ACCESS_KEY_ID,
    secretAccessKey: process.env.BACKUP_SECRET_ACCESS_KEY,
  };

  if (dedicated.bucket && dedicated.endpoint && dedicated.accessKeyId && dedicated.secretAccessKey) {
    return {
      bucket: dedicated.bucket,
      region: process.env.BACKUP_REGION ?? 'auto',
      endpoint: dedicated.endpoint,
      accessKeyId: dedicated.accessKeyId,
      secretAccessKey: dedicated.secretAccessKey,
      sharedWithMedia: false,
    };
  }

  const bucket = process.env.PLATFORM_R2_BUCKET;
  const endpoint = process.env.PLATFORM_R2_ENDPOINT;
  const accessKeyId = process.env.PLATFORM_R2_ACCESS_KEY_ID;
  const secretAccessKey = process.env.PLATFORM_R2_SECRET_ACCESS_KEY;

  if (!bucket || !endpoint || !accessKeyId || !secretAccessKey) return null;

  return {
    bucket,
    region: process.env.PLATFORM_R2_REGION ?? 'auto',
    endpoint,
    accessKeyId,
    secretAccessKey,
    sharedWithMedia: true,
  };
}

/** The bucket backups actually land in, whichever credential set won. */
export function backupBucket(): string | null {
  return readBackupStorageEnv()?.bucket ?? null;
}

/**
 * Which environment's backups this process writes.
 *
 * Defaults to `test`, matching MEDIA_ENV_PREFIX. Defaulting to the safe side
 * matters more here than it does for media: the failure mode of a mis-set
 * prefix is not a stray image on a site, it is a developer's dump landing in
 * the production backup store — or, worse, a retention job there deleting real
 * backups because it counted a dev machine's files against the budget.
 */
export function backupEnvPrefix(): string {
  // Falls back to MEDIA_ENV_PREFIX before 'test': when one bucket holds both,
  // media and backups are by definition in the same environment, and letting
  // them drift apart would put `production` backups in a `test` bucket that
  // media already considers test.
  const raw = process.env.BACKUP_ENV_PREFIX ?? process.env.MEDIA_ENV_PREFIX ?? 'test';
  return raw.trim().toLowerCase();
}

export function isProductionBackupEnv(): boolean {
  return backupEnvPrefix() === 'production';
}

/** `test/database/backups` or `production/database/backups`. */
export function backupRootPrefix(): string {
  return `${backupEnvPrefix()}/database/backups`;
}

export function dumpPrefix(): string {
  return `${backupRootPrefix()}/databases`;
}

/**
 * Physical base backups: `<env>/database/backups/base/<timestamp>/`.
 *
 * A directory per backup rather than a single object, because pg_basebackup
 * produces three files (the compressed tarball, the streamed WAL, and the
 * manifest) and all three are needed to restore.
 */
export function basePrefix(): string {
  return `${backupRootPrefix()}/base`;
}

export function baseBackupPrefix(timestamp: string): string {
  return `${basePrefix()}/${timestamp}`;
}

export function walPrefix(): string {
  return `${backupRootPrefix()}/wal`;
}

/**
 * WAL object naming: `wal_<segment>.gz`.
 *
 *   test/database/backups/wal/wal_000000010000000000000065.gz
 *
 * ── WHY THE 24-CHARACTER SEGMENT NAME IS NOT NEGOTIABLE ─────────────────────
 *
 * It is tempting to name these with a counter or a timestamp, but the segment
 * name is not decoration — it is PostgreSQL's identifier for that exact slice
 * of the write-ahead log. During recovery Postgres asks `restore_command` for a
 * segment BY NAME, substituting it as `%f`, and it asks in strict order. A file
 * we cannot map back to its original name is a file recovery can never request,
 * so the whole archive becomes unusable for its only purpose.
 *
 * The name also encodes the ordering (timeline + logical file + segment), which
 * is what the gap detector in RecoveryWindowService compares to notice a
 * MISSING segment — the failure that silently voids point-in-time recovery.
 *
 * So: a `wal_` prefix for legibility, the segment name untouched, and `.gz`
 * because the content genuinely is gzipped (measured 47:1 on this database).
 * The earlier `.walfomat` is still read, so an archive written before this
 * change stays restorable.
 */
export const WAL_NAME_PREFIX = 'wal_';
export const WAL_EXTENSION = '.gz';

/** Extensions this store has used. Newest first; all are still readable. */
const WAL_EXTENSIONS = ['.gz', '.walfomat'];

export function walFileName(segmentName: string): string {
  return `${WAL_NAME_PREFIX}${segmentName}${WAL_EXTENSION}`;
}

/**
 * Recover the segment name from a stored filename.
 *
 * Tolerant on purpose: it accepts the current `wal_<segment>.gz`, the older
 * `<segment>.walfomat`, and a bare segment name. Reading an archive written by
 * a previous version of this code is exactly the situation a restore is for.
 */
export function segmentNameFromFile(filename: string): string {
  let name = filename;

  if (name.startsWith(WAL_NAME_PREFIX)) name = name.slice(WAL_NAME_PREFIX.length);
  for (const ext of WAL_EXTENSIONS) {
    if (name.endsWith(ext)) {
      name = name.slice(0, -ext.length);
      break;
    }
  }

  return name;
}

/**
 * Refuse to start when the bucket and the prefix disagree about which
 * environment this is.
 *
 * Exactly the reasoning in `assertStorageEnvironmentConsistent` for media, and
 * it applies with more force to backups. An R2 token with account-wide object
 * access reaches every bucket, so `BACKUP_BUCKET=production` on a developer
 * machine is enough to write into — and expire from — the live backup store.
 * Nothing about that fails on its own: the upload succeeds, the retention job
 * runs, and real backups are quietly deleted to make room for a laptop's dumps.
 *
 * Failing at boot is the point. A server that refuses to start gets noticed in
 * seconds; one that starts and prunes the wrong backup store gets noticed
 * during a restore.
 *
 * Silent when the bucket is not named after an environment — a single shared
 * bucket separated only by prefix is a legitimate setup this check has no
 * opinion about.
 */
export function assertBackupEnvironmentConsistent(): void {
  // The EFFECTIVE bucket, not just BACKUP_BUCKET — otherwise a deployment
  // falling back to PLATFORM_R2_BUCKET would skip this check entirely, which
  // is the configuration most likely to be wrong.
  const bucket = backupBucket()?.trim().toLowerCase();
  const prefix = backupEnvPrefix();

  if (!bucket || !ENVIRONMENT_PREFIXES.includes(bucket)) return;
  if (bucket === prefix) return;

  throw new Error(
    `Backup environment mismatch: backup bucket="${bucket}" but ` +
      `BACKUP_ENV_PREFIX="${prefix}". These must agree — a bucket named after an ` +
      `environment is that environment. Writing "${prefix}" backups into the ` +
      `"${bucket}" store is almost certainly a mistake, and a token with ` +
      `account-wide object access would let it succeed silently, including the ` +
      `retention job that deletes to stay inside quota. ` +
      `Set both to "${bucket}", or both to "${prefix}". ` +
      `(The bucket comes from BACKUP_BUCKET, or PLATFORM_R2_BUCKET when that is unset; ` +
      `the prefix from BACKUP_ENV_PREFIX, or MEDIA_ENV_PREFIX when that is unset.)`
  );
}
